import React, { useState } from 'react';
import { 
    Box, Container, Typography, Grid, TextField, MenuItem, 
    Button, CircularProgress, Divider, InputAdornment 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTruckFast, faUser, faMapMarkerAlt, 
    faBoxOpen, faPrint, faArrowLeft 
} from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createDispatch, searchVehicle } from '../../../controllers/help/DispatchController';

const VEHICLE_OPTIONS = {
    'Vehículo Ligero / Menor': [
        'Moto', 
        'Vehículo particular (Sedán/Hatchback)', 
        'Camioneta Pick-up', 
        'Van / Furgoneta'
    ],
    'Carga Mediana': [
        'Camión 350 / Tritón', 
        'Camión NPR / NKR (Cava cerrada)', 
        'Camión NPR / NKR (Plataforma/Estacas)'
    ],
    'Carga Pesada': [
        'Camión 750 (o similar)', 
        'Gandola / Tractocamión (Cava)', 
        'Gandola / Tractocamión (Plataforma)', 
        'Camión Volteo'
    ]
};

export default function DispatchCreateView() {
    const history = useHistory();
    const [isSearching, setIsSearching] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        plate: '',
        vehicle_category: '',
        vehicle_type: '',
        driver_name: '',
        driver_ci: '',
        driver_phone: '58',
        destination_city: '',
        delivery_point: '',
        cargo_description: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newFormData = { ...formData, [name]: value };

        if (name === 'vehicle_category') {
            newFormData.vehicle_type = '';
        }

        setFormData(newFormData);
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleQuillChange = (value) => {
        setFormData(prev => ({ ...prev, cargo_description: value }));
        if (errors.cargo_description) {
            setErrors(prev => ({ ...prev, cargo_description: null }));
        }
    };

    const isQuillEmpty = (htmlString) => {
        if (!htmlString) return true;
        const textOnly = htmlString.replace(/<[^>]*>/g, '').trim();
        return textOnly.length === 0;
    };

    // La función ahora retorna los errores al estilo del componente de Ayuda
    const validateForm = () => {
        let newErrors = {};

        if (!formData.plate.trim()) newErrors.plate = "La placa es obligatoria.";
        if (!formData.vehicle_category) newErrors.vehicle_category = "Seleccione una categoría.";
        if (!formData.vehicle_type) newErrors.vehicle_type = "Seleccione un tipo específico.";
        if (!formData.driver_name.trim()) newErrors.driver_name = "El nombre es obligatorio.";
        
        // Validación exacta de Cédula de tu código (Sin guion y 7-8 dígitos)
        if (!formData.driver_ci.trim()) {
            newErrors.driver_ci = "La cédula es obligatoria.";
        } else if (!/^[VEve][0-9]{7,8}$/.test(formData.driver_ci.trim())) {
            newErrors.driver_ci = "Formato inválido. Ej: V12345678";
        }

        // Validación exacta de Teléfono de tu código (Código 58 + Operadora venezolana)
        if (!formData.driver_phone.trim()) {
            newErrors.driver_phone = "El teléfono es obligatorio.";
        } else if (!/^58(414|424|412|422|416|426)[0-9]{7}$/.test(formData.driver_phone.trim())) {
            newErrors.driver_phone = "Debe ser 58 + Operadora + Número (Ej: 584241234567).";
        }

        if (!formData.destination_city.trim()) newErrors.destination_city = "La ciudad de destino es obligatoria.";
        if (!formData.delivery_point.trim()) newErrors.delivery_point = "El punto de enlace es obligatorio.";
        
        if (isQuillEmpty(formData.cargo_description)) {
            newErrors.cargo_description = "Describa detalladamente la carga a enviar.";
        }

        setErrors(newErrors);
        return newErrors;
    };

    const handlePlateSearch = async () => {
        const cleanPlate = formData.plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (cleanPlate.length < 4) return;

        setIsSearching(true);
        try {
            // Llamada real al backend usando el controlador
            const response = await searchVehicle(cleanPlate);
            
            // Si la respuesta es exitosa y el vehículo existe
            if (response && response.success && response.data) {
                const vehicle = response.data;
                
                setFormData(prev => ({
                    ...prev,
                    vehicle_category: vehicle.category || '',
                    vehicle_type: vehicle.type || '',
                    driver_name: vehicle.default_driver_name || '',
                    driver_ci: vehicle.default_driver_ci || '',
                    driver_phone: vehicle.default_driver_phone || '58',
                }));
                
                // Limpiamos los errores porque autocompletamos con datos válidos
                setErrors({});
            }
            // Si el vehículo no existe, el sistema simplemente no hace nada 
            // y permite al usuario llenar los campos manualmente sin fricciones.

        } catch (error) {
            console.error("Error buscando placa:", error);
        } finally {
            // Esto asegura que el spinner de carga se detenga siempre
            setIsSearching(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // 1. Validamos localmente antes de intentar enviar al servidor
        const formErrors = validateForm();
        
        if (Object.keys(formErrors).length > 0) {
            const firstErrorField = Object.keys(formErrors)[0];
            
            let errorElement;
            if (firstErrorField === 'cargo_description') {
                errorElement = document.getElementById('cargo_description_wrapper');
            } else {
                errorElement = document.getElementsByName(firstErrorField)[0];
            }
            
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Solo ejecutamos focus si es un elemento que lo permite
                if (errorElement.tagName === 'INPUT' || errorElement.tagName === 'TEXTAREA') {
                    errorElement.focus();
                }
            }
            return;
        }

        // 2. Iniciamos el proceso de envío
        setIsSubmitting(true);
        
        try {
            // Llamada al controlador que acabamos de crear
            const response = await createDispatch(formData);

            if (response && response.success) {
                // Éxito: Navegamos de vuelta a la lista de despachos
                history.push('/dispatches');
            } else {
                // Manejo de error de validación o lógica desde Laravel
                console.error("Error devuelto por el servidor:", response);
                setIsSubmitting(false);
                // Opcional: mostrar un Toast o Alert aquí con response.message
            }
            
        } catch (error) {
            console.error("Error crítico al guardar el despacho:", error);
            setIsSubmitting(false);
            // Aquí puedes manejar errores de red (e.g., setStatus({error: 'Error de conexión'}))
        }
    };

    const currentTypeOptions = formData.vehicle_category ? VEHICLE_OPTIONS[formData.vehicle_category] : [];

    return (
        <Container maxWidth="xl" sx={{ mt: 5, pb: 6, bgcolor:'#FFF' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                <Button 
                    variant="outlined" 
                    onClick={() => history.goBack()}
                    sx={{ minWidth: '40px', width: '40px', height: '40px', p: 0, borderRadius: 2 }}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </Button>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a1a', fontSize: { xs: '1.8rem', md: '2.125rem' } }}>
                        Registrar Salida
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        Ingresa la placa para autocompletar o registra un nuevo vehículo.
                    </Typography>
                </Box>
            </Box>

            <form onSubmit={handleSubmit} noValidate>
                {/* SECCIÓN 1: Vehículo */}
                <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FontAwesomeIcon icon={faTruckFast} color="#1565c0" /> 1. Datos del Vehículo
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Placa del Vehículo"
                                name="plate"
                                value={formData.plate}
                                onChange={handleChange}
                                onBlur={handlePlateSearch}
                                error={!!errors.plate}
                                helperText={errors.plate}
                                InputProps={{
                                    endAdornment: isSearching ? (
                                        <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                label="Categoría"
                                name="vehicle_category"
                                value={formData.vehicle_category}
                                onChange={handleChange}
                                error={!!errors.vehicle_category}
                                helperText={errors.vehicle_category}
                            >
                                {Object.keys(VEHICLE_OPTIONS).map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                label="Tipo Específico"
                                name="vehicle_type"
                                value={formData.vehicle_type}
                                onChange={handleChange}
                                error={!!errors.vehicle_type}
                                helperText={errors.vehicle_type || (!formData.vehicle_category ? "Seleccione primero una categoría" : "")}
                                disabled={!formData.vehicle_category}
                                SelectProps={{
                                    sx: { '& .MuiSelect-select': { whiteSpace: 'normal', wordBreak: 'break-word' } }
                                }}
                            >
                                {currentTypeOptions.map((type) => (
                                    <MenuItem key={type} value={type} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* SECCIÓN 2: Conductor */}
                <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FontAwesomeIcon icon={faUser} color="#1565c0" /> 2. Datos del Conductor
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <TextField fullWidth label="Nombre Completo" name="driver_name" value={formData.driver_name} onChange={handleChange} error={!!errors.driver_name} helperText={errors.driver_name} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField 
                                fullWidth 
                                label="Cédula" 
                                name="driver_ci" 
                                value={formData.driver_ci} 
                                onChange={handleChange} 
                                error={!!errors.driver_ci} 
                                helperText={errors.driver_ci || "Debe comenzar con V o E"} 
                                placeholder="Ej: V12345678" 
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField 
                                fullWidth 
                                label="Teléfono (WhatsApp)" 
                                name="driver_phone" 
                                value={formData.driver_phone} 
                                onChange={handleChange} 
                                error={!!errors.driver_phone} 
                                helperText={errors.driver_phone || "Código 58 + Operadora + Número"} 
                                placeholder="Ej: 584141234567"
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* SECCIÓN 3: Logística */}
                <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} color="#1565c0" /> 3. Destino y Recepción
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Ciudad Destino (Ej: La Guaira)" name="destination_city" value={formData.destination_city} onChange={handleChange} error={!!errors.destination_city} helperText={errors.destination_city} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Punto de Enlace (Persona o Lugar)" name="delivery_point" value={formData.delivery_point} onChange={handleChange} error={!!errors.delivery_point} helperText={errors.delivery_point} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <FontAwesomeIcon icon={faBoxOpen} color="#1565c0" /> Resumen de Carga
                                </Typography>
                                
                                {/* Contenedor con ID para poder hacerle focus/scroll si falla la validación */}
                                <Box 
                                    id="cargo_description_wrapper"
                                    sx={{ 
                                        '.ql-toolbar': { 
                                            borderRadius: '8px 8px 0 0', 
                                            borderColor: errors.cargo_description ? '#d32f2f' : '#c4c4c4' 
                                        }, 
                                        '.ql-container': { 
                                            borderRadius: '0 0 8px 8px', 
                                            borderColor: errors.cargo_description ? '#d32f2f' : '#c4c4c4',
                                            minHeight: '220px', // <-- Aumentado para darle más líneas
                                            fontSize: '16px',
                                            fontFamily: 'inherit'
                                        },
                                        '.ql-editor': {
                                            minHeight: '220px'  // <-- Asegura que el área de escritura ocupe todo el espacio
                                        }
                                    }}
                                >
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.cargo_description}
                                        onChange={handleQuillChange}
                                        placeholder="Detalla aquí los insumos... (Ej: 50 Colchones, 20 Cajas de agua)"
                                    />
                                </Box>
                                
                                {errors.cargo_description && (
                                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 1, ml: 2, display: 'block' }}>
                                        {errors.cargo_description}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Acciones */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" size="large" onClick={() => history.push('/help/dispatches')} sx={{ borderRadius: 2, fontWeight: 600 }}>
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary" size="large" disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <FontAwesomeIcon icon={faPrint} />} sx={{ borderRadius: 2, fontWeight: 600, px: 4 }}>
                        Guardar e Imprimir Guía
                    </Button>
                </Box>
            </form>
        </Container>
    );
}